var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { InteractionsServiceConstants, SERVICE } from "@agentx/agentx-services";
import { action, makeObservable, observable, ObservableMap } from "mobx";
import { logger } from "../logger/store-logger";
import { mapContactStatsToStoreData, mapTaskDetailsToStoreData } from "./interactions-store.utils";
const INTERACTIONS_CLEANUP_DELAY = 1000;
export class InteractionsStore {
    constructor() {
        this.selectedInteraction = undefined;
        this.selectedTab = undefined;
        //observable.map was chosen to allow for a simpler integration with AGGrid, do not use unless you have a similar requirement
        this.activeInteractions = observable.map();
        this.queuedInteractions = observable.map();
        this.completedInteractions = observable.map();
        this.cleanupTimerId = undefined;
        this.subscriptionIsInProgress = false;
        this.subscriptionId = undefined;
        makeObservable(this);
    }
    subscribeToInteractions() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.cleanupTimerId) {
                clearTimeout(this.cleanupTimerId);
                this.cleanupTimerId = undefined;
            }
            if (this.subscriptionId || this.subscriptionIsInProgress) {
                return; // Already subscribed
            }
            this.subscriptionIsInProgress = true;
            try {
                const updateCallback = this.createUpdateCallbackFunc();
                const subscriptionId = yield SERVICE.interactions.subscribeInteractionUpdates(updateCallback);
                this.subscriptionId = subscriptionId;
                this.subscriptionIsInProgress = false;
            }
            catch (error) {
                logger.error("Error subscribing to interactions", error);
                this.subscriptionIsInProgress = false;
                throw error;
            }
        });
    }
    stopInteractionUpdates() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.subscriptionId) {
                if (this.cleanupTimerId) {
                    clearTimeout(this.cleanupTimerId);
                }
                this.cleanupTimerId = setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    this.cleanInteractionsMaps();
                    this.cleanupTimerId = undefined;
                    if (this.subscriptionId) {
                        try {
                            yield SERVICE.interactions.stopInteractionUpdates(this.subscriptionId);
                            this.subscriptionId = undefined;
                        }
                        catch (error) {
                            logger.error("Error stopping interaction updates", error);
                        }
                    }
                }), INTERACTIONS_CLEANUP_DELAY);
            }
        });
    }
    cleanInteractionsMaps() {
        var _a;
        if (((_a = this.selectedInteraction) === null || _a === void 0 ? void 0 : _a.id) &&
            (this.activeInteractions.has(this.selectedInteraction.id) ||
                this.queuedInteractions.has(this.selectedInteraction.id))) {
            this.selectedInteraction = undefined;
        }
        this.getInteractionsMap(InteractionsServiceConstants.QueryType.Active).clear();
        this.getInteractionsMap(InteractionsServiceConstants.QueryType.Queued).clear();
    }
    fetchCompletedInteractions(startTime, endTime, sortBy, filters) {
        return __awaiter(this, void 0, void 0, function* () {
            const currentQuery = { startTime, endTime, sortBy, filters };
            const paramsAreChanged = JSON.stringify(this.lastCompletedInteractionsQuery) !== JSON.stringify(currentQuery);
            if (paramsAreChanged) {
                logger.debug("[InteractionsStore] Search params changed. Purging completed interactions");
                this.lastCompletedInteractionsQuery = currentQuery;
                this.completedInteractions.clear();
                this.completedNextPage = undefined;
            }
            if (this.completedNextPage) {
                return this.completedNextPage();
            }
            try {
                const { fetchMore } = yield SERVICE.interactions.fetchCompletedInteractions(startTime, endTime, (data) => this.handleCompletedCallback(data), sortBy, filters);
                this.completedNextPage = fetchMore;
                return true;
            }
            catch (error) {
                logger.error("Error fetching completed interactions.", error.message);
                throw error;
            }
        });
    }
    setSelectedInteraction(interactionId) {
        if (interactionId !== "") {
            const activeInteraction = this.activeInteractions.get(interactionId);
            if (activeInteraction) {
                this.selectedInteraction = activeInteraction;
                this.selectedTab = InteractionsServiceConstants.QueryType.Active;
                return;
            }
            const queuedInteraction = this.queuedInteractions.get(interactionId);
            if (queuedInteraction) {
                this.selectedInteraction = queuedInteraction;
                this.selectedTab = InteractionsServiceConstants.QueryType.Queued;
                return;
            }
            const completedInteraction = this.completedInteractions.get(interactionId);
            if (completedInteraction) {
                this.selectedInteraction = completedInteraction;
                this.selectedTab = InteractionsServiceConstants.QueryType.Completed;
                return;
            }
        }
        else {
            this.selectedInteraction = undefined;
            this.selectedTab = undefined;
        }
    }
    handleCompletedCallback(data) {
        data.forEach((interaction) => {
            this.completedInteractions.set(interaction.id, mapTaskDetailsToStoreData(interaction));
        });
    }
    createUpdateCallbackFunc() {
        return (data) => {
            var _a;
            if (data.fullUpdate) {
                this.handleFullUpdate(data.fullUpdate);
            }
            else if (data.realTimeUpdate) {
                this.handleUpdate(data.realTimeUpdate.updated);
            }
            if ((_a = data.realTimeUpdate) === null || _a === void 0 ? void 0 : _a.removed) {
                this.handleRemove(data.realTimeUpdate.removed);
            }
        };
    }
    handleFullUpdate(fullUpdate) {
        const assignedTasks = fullUpdate.filter((item) => item.data.contactRoutingState === "Assigned");
        this.activeInteractions = observable.map(assignedTasks.map((item) => [item.data.interactionId, mapContactStatsToStoreData(item)]));
        const queuedTasks = fullUpdate.filter((item) => item.data.contactRoutingState === "Queued");
        this.queuedInteractions = observable.map(queuedTasks.map((item) => [item.data.interactionId, mapContactStatsToStoreData(item)]));
    }
    handleUpdate(update) {
        var _a, _b, _c;
        //TODO: This logic needs to move into the service layer
        const tasksByState = update.reduce((acc, task) => {
            const state = task.data.contactRoutingState ||
                InteractionsServiceConstants.ContactRoutingState.Other;
            acc[state] = acc[state] || [];
            acc[state].push(task);
            return acc;
        }, {});
        if ((_a = tasksByState.Other) === null || _a === void 0 ? void 0 : _a.length) {
            for (const task of tasksByState.Other) {
                this.deleteRTDInteraction(task.data.interactionId);
            }
        }
        if ((_b = tasksByState.Assigned) === null || _b === void 0 ? void 0 : _b.length) {
            for (const task of tasksByState.Assigned) {
                this.queuedInteractions.delete(task.data.interactionId);
            }
            this.updateInteractionsMapFromRTD(this.activeInteractions, tasksByState.Assigned);
        }
        if ((_c = tasksByState.Queued) === null || _c === void 0 ? void 0 : _c.length) {
            this.updateInteractionsMapFromRTD(this.queuedInteractions, tasksByState.Queued);
        }
    }
    handleRemove(removed) {
        this.updateInteractionsMap(this.activeInteractions, undefined, removed);
        this.updateInteractionsMap(this.queuedInteractions, undefined, removed);
    }
    getInteractionsMap(queryType) {
        return queryType === InteractionsServiceConstants.QueryType.Active
            ? this.activeInteractions
            : this.queuedInteractions;
    }
    updateInteractionsMap(targetMap, updated, removed) {
        if (updated) {
            updated.forEach((interaction) => {
                targetMap.set(interaction.id, mapTaskDetailsToStoreData(interaction));
            });
        }
        if (removed) {
            removed.forEach((interactionId) => {
                this.deleteRTDInteraction(interactionId, targetMap);
            });
        }
    }
    updateInteractionsMapFromRTD(targetMap, updated, removed) {
        if (updated) {
            updated.forEach((interaction) => {
                targetMap.set(interaction.data.interactionId, mapContactStatsToStoreData(interaction));
            });
        }
        if (removed) {
            removed.forEach((interactionId) => {
                this.deleteRTDInteraction(interactionId, targetMap);
            });
        }
    }
    deleteRTDInteraction(interactionId, target) {
        var _a;
        if (target instanceof ObservableMap) {
            target.delete(interactionId);
        }
        else if (target) {
            this.getInteractionsMap(target).delete(interactionId);
        }
        else {
            this.activeInteractions.delete(interactionId);
            this.queuedInteractions.delete(interactionId);
        }
        if (((_a = this.selectedInteraction) === null || _a === void 0 ? void 0 : _a.id) === interactionId) {
            this.selectedInteraction = undefined;
        }
    }
}
__decorate([
    observable
], InteractionsStore.prototype, "selectedInteraction", void 0);
__decorate([
    observable
], InteractionsStore.prototype, "selectedTab", void 0);
__decorate([
    observable.ref
], InteractionsStore.prototype, "activeInteractions", void 0);
__decorate([
    observable.ref
], InteractionsStore.prototype, "queuedInteractions", void 0);
__decorate([
    observable.ref
], InteractionsStore.prototype, "completedInteractions", void 0);
__decorate([
    action
], InteractionsStore.prototype, "subscribeToInteractions", null);
__decorate([
    action
], InteractionsStore.prototype, "stopInteractionUpdates", null);
__decorate([
    action
], InteractionsStore.prototype, "cleanInteractionsMaps", null);
__decorate([
    action
], InteractionsStore.prototype, "fetchCompletedInteractions", null);
__decorate([
    action
], InteractionsStore.prototype, "setSelectedInteraction", null);
__decorate([
    action
], InteractionsStore.prototype, "handleCompletedCallback", null);
__decorate([
    action
], InteractionsStore.prototype, "handleFullUpdate", null);
__decorate([
    action
], InteractionsStore.prototype, "handleUpdate", null);
__decorate([
    action
], InteractionsStore.prototype, "handleRemove", null);
__decorate([
    action
], InteractionsStore.prototype, "updateInteractionsMap", null);
__decorate([
    action
], InteractionsStore.prototype, "updateInteractionsMapFromRTD", null);
__decorate([
    action
], InteractionsStore.prototype, "deleteRTDInteraction", null);
